/**
 * سربرگِ منابع — «هر کاندیدا کجای راه است، و چه کارش می‌شود کرد».
 *
 * ═══ چرا یک ردیف و نه چهار جدول ═══
 *
 * زندگیِ یک منبع چهار مرحله دارد — پیش‌نویس، غربالگری، ترفیع، رجیستری — و
 * تا امروز هر کدام جای دیگری بود: یک فایلِ YAML، یک خروجیِ ترمینال، یک فایلِ
 * دیگر. سوالی که هیچ‌کدام جواب نمی‌دادند این بود: «این سایت را قبلاً دیده‌ایم
 * و چه شد؟» جوابش فقط وقتی وجود دارد که هر چهار مرحله در **یک ردیف** باشند.
 * چهار جدولِ جدا همان وضعیتِ قبلی است با ظاهرِ بهتر.
 *
 * ═══ چرا ویرایش «ثبت می‌شود» و همان لحظه اعمال نمی‌شود ═══
 *
 * `crawler.source_versions` سه نامتغیر دارد که در پایتون زندگی می‌کنند و از
 * SQL ساختنی نیستند — مهم‌ترینشان `behavior_hash` که روی سریال‌سازیِ دقیقِ
 * `json.dumps` پایتون حساب می‌شود. توضیحِ کاملش بالای مایگریشن 0076 است.
 * پس ویرایش در `crawler_jobs` می‌نشیند و کرالر اعمالش می‌کند؛ رابط این را
 * صریح می‌گوید، چون «ذخیره شد» برای چیزی که هنوز اعمال نشده، دروغ است.
 *
 * تنها استثنا افزودنِ پیش‌نویس است که همان لحظه ثبت می‌شود.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sourceRepo, type CrawlerJob, type PipelineCity } from '@/core/data/sourceRepo';
import {
  JOB_LABEL, STAGE_HELP, STAGE_LABEL, STAGE_ORDER, TODO_HELP, TODO_LABEL,
  rejectionReason, stageOf,
  type PipelineRow, type SourceStage,
} from '@/core/lib/sourcePipeline';
import { reportError } from '@/core/lib/report';
import { useUrlList, useUrlState } from '@/app/useUrlState';
import {
  needOf, NEED_KIND_LABEL, NEED_LABEL, NEED_ORDER, type NeedKey,
} from '@/core/lib/sourceTest';
import { Modal } from '@/ui/Modal';
import { SourceDetail } from './SourceDetail';
import { AddDraftForm } from './AddDraftForm';

const fmt = (n: number | null | undefined) =>
  (typeof n === 'number' ? n.toLocaleString('en-US') : '—');

const ago = (iso: string | null): string => {
  if (!iso) return '—';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins} دقیقه پیش`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} ساعت پیش`;
  return `${Math.round(hours / 24)} روز پیش`;
};

/**
 * «۲۴ ساعتِ گذشته / سرجمع» — و صفرِ سمتِ چپ عمداً کم‌رنگ **نیست**.
 *
 * ⚠️ کلِ دلیلِ وجودِ عددِ اول همین حالت است: منبعی که دیشب هیچ نیاورده
 * ولی سرجمعش بزرگ است، از ستونِ سرجمع سالم به نظر می‌رسد. اگر صفرش هم
 * مثلِ بقیه خاکستری شود، همان‌جا گم می‌شود که قرار بود دیده شود.
 */
function Pair({ now, all }: { now: number; all: number }) {
  return (
    <>
      <span className={now === 0 && all > 0 ? 'bad' : ''}>{fmt(now)}</span>
      <span className="dim"> / {fmt(all)}</span>
    </>
  );
}

/**
 * «این منبع چه چیزی لازم دارد» — تشخیص از آخرین تستش.
 *
 * ⚠️ رنگ روی **جنسِ کار** است، نه شدتِ خرابی: کاری که با یک کلیک درست
 * می‌شود نباید مثلِ کاری که هفته‌ای کد می‌خواهد دیده شود. همین یک تفکیک
 * است که این ستون را از «یک برچسبِ دیگر» به «صفِ کار» تبدیل می‌کند.
 */
function NeedChip({ need }: { need: ReturnType<typeof needOf> }) {
  if (!need) {
    // ⚠️ «تست نشده» با «سالم» یکی نیست و نباید یک شکل دیده شود.
    return <span className="dim" style={{ fontSize: 12 }}>تست نشده</span>;
  }
  const tone = need.kind === 'code' ? 'chip warn'
    : need.kind === 'none' ? 'chip on' : 'chip';
  return (
    <span className={tone} title={`${NEED_KIND_LABEL[need.kind]} — ${need.why}

${need.todo}`}>
      {need.label}
    </span>
  );
}

/** سطلِ منبعِ بی‌شهر، همان چیزی که 0069 ساخت. */
const cityLabel = (c: string) => (c === '~unknown' ? 'بدونِ شهر' : c);

/** رنگِ نشانِ مرحله. فقط دو حالت رنگ می‌گیرند: چیزی که کار می‌کند و چیزی که رد شد. */
/**
 * رنگِ نشانِ مرحله. فقط سه حالت رنگ می‌گیرند و بقیه خنثی می‌مانند.
 *
 * ⚠️ «منتظرِ کد» همان زردِ «ردشده» را می‌گیرد و نه یک رنگِ تازه: هر دو
 * یعنی «امشب از این ردیف چیزی درنمی‌آید». تفاوتشان در متن است، نه در
 * رنگ — و رنگِ چهارم فقط جدول را شلوغ می‌کرد.
 */
const stageClass = (s: SourceStage) =>
  (s === 'live' ? 'chip on'
    : s === 'rejected' || s === 'parked' ? 'chip warn'
      : 'chip');

function JobQueue({ jobs, onCancel }: { jobs: CrawlerJob[]; onCancel: (id: number) => void }) {
  if (jobs.length === 0) {
    return <p className="dim">هیچ کاری ثبت نشده.</p>;
  }
  return (
    <div className="scrollx">
      <table className="tbl">
        <thead>
          <tr><th>کار</th><th>هدف</th><th>وضعیت</th><th>کِی</th><th>نتیجه</th><th /></tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id}>
              <td>{JOB_LABEL[j.kind] ?? j.kind}</td>
              <td className="mono dim">{j.target ?? 'همه'}</td>
              <td className={j.status === 'failed' ? 'bad' : ''}>{j.status}</td>
              <td className="dim">{ago(j.requested_at)}</td>
              <td className="dim mono">
                {j.error ? <span className="err">{j.error.slice(0, 80)}</span>
                  : j.result ? JSON.stringify(j.result).slice(0, 80) : ''}
              </td>
              <td>
                {j.status === 'queued' ? (
                  <button className="btn ghost" type="button"
                    onClick={() => onCancel(j.id)}>لغو</button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SourcesTab() {
  const qc = useQueryClient();
  // ⚠️ شهر و جستجو و فیلترِ مرحله و ردیفِ باز، همه در آدرس‌اند. این صفحه
  // را آدم باز می‌گذارد و رفرش می‌کند؛ پریدنِ فیلترها یعنی هر بار از نو
  // چیدنشان. ضمناً لینکِ «این ردیف را ببین» هم از همین درمی‌آید.
  const [city, setCity] = useUrlState('city', '');
  const [q, setQ] = useUrlState('q', '');
  const [stages, setStages] = useUrlList('stage', STAGE_ORDER) as
    [SourceStage[], (v: SourceStage[]) => void];
  const [needs, setNeeds] = useUrlList('need', NEED_ORDER) as
    [NeedKey[], (v: NeedKey[]) => void];
  /**
   * فیلترِ «فقط آن‌هایی که فرستاده‌ام سراغِ کد».
   *
   * ⚠️ جدا از فیلترِ تگِ خودکار است و باید باشد: آن یکی حدسِ ابزار را
   * فیلتر می‌کند، این یکی تصمیمِ آدم را. وقتی می‌خواهی بنشینی و پارسر
   * بنویسی، این فهرست است که لازم داری — نه حدس‌ها.
   */
  const [todoOnly, setTodoOnly] = useUrlState('todo', '');
  const [openKey, setOpenKey] = useUrlState('open', '');
  const open = openKey || null;
  const setOpen = (v: string | null) => setOpenKey(v ?? '');
  /**
   * ردیفی که دکمه‌ی «تست»ش زده شده.
   *
   * ⚠️ جدا از `open` است و نه یک boolean کنارش: باز کردنِ ردیف با کلیک
   * روی نام **نباید** تست بزند — تستِ ناخواسته یعنی درخواستِ ناخواسته به
   * سایتِ طرف. فقط ردیفی که این‌جا نشسته باشد خودکار شروع می‌کند.
   */
  const [testing, setTesting] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const cities = useQuery({ queryKey: ['srcCities'], queryFn: sourceRepo.cities });
  const pipeline = useQuery({
    queryKey: ['srcPipeline', city, q],
    queryFn: () => sourceRepo.pipeline(city, q),
  });
  /**
   * ⚠️ **این کوئری تنها چیزی است که «کارِ من رفت؟» را جواب می‌دهد.**
   *
   * ردیف‌های جدول از کشِ گزارش می‌آیند (مایگریشنِ ۰۰۹۵)، که شب ساخته
   * می‌شود — پس ستونِ `jobs`ِ داخلِ آن‌ها مالِ **دیشب** است و کاری که
   * همین الان ثبت شده هیچ‌وقت در آن نمی‌آید. تا امروز نشانِ «در صف» از
   * همان می‌خواند و برای همین هرگز ظاهر نمی‌شد.
   *
   * ‏`admin_source_jobs` اما مستقیم `public.crawler_jobs` را می‌خواند و
   * زنده است. پس نشان از این‌جا می‌آید، نه از ردیف.
   */
  const jobs = useQuery({
    queryKey: ['srcJobs'],
    queryFn: () => sourceRepo.jobs(40),
    // فقط وقتی چیزی در جریان است. صفِ خالی نباید تا ابد هر سه ثانیه
    // یک کوئری بزند.
    refetchInterval: (q) => {
      const list = (q.state.data as CrawlerJob[] | undefined) ?? [];
      return list.some((j) => j.status === 'queued' || j.status === 'running')
        ? 3000 : false;
    },
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['srcPipeline'] });
    qc.invalidateQueries({ queryKey: ['srcJobs'] });
    qc.invalidateQueries({ queryKey: ['srcCities'] });
  };

  const queue = useMutation({
    mutationFn: (v: { kind: CrawlerJob['kind']; target?: string | null }) =>
      sourceRepo.queue(v.kind, v.target ?? null),
    onSuccess: (job) => {
      setMsg(`«${JOB_LABEL[job.kind] ?? job.kind}» برای ${job.target ?? 'همه'} در صف نشست.`);
      refresh();
    },
    onError: (err) => {
      reportError('admin.sources.queue', err);
      setMsg(err instanceof Error ? err.message : String(err));
    },
  });

  const cancel = useMutation({
    mutationFn: (id: number) => sourceRepo.cancelJob(id),
    onSuccess: refresh,
    onError: (err) => reportError('admin.sources.cancel', err),
  });

  const live = jobs.data ?? [];
  const running = live.filter((j) => j.status === 'queued' || j.status === 'running');

  /**
   * نگاشتِ هدف → کارهای در جریان. هدفِ `inspect`/`test_source`/`promote`
   * یا `draft_id` است یا `bare`، پس ردیف هر دو را می‌پرسد.
   */
  const busy = useMemo(() => {
    const map = new Map<string, CrawlerJob[]>();
    for (const job of running) {
      if (!job.target) continue;
      map.set(job.target, [...(map.get(job.target) ?? []), job]);
    }
    return map;
  }, [running.map((j) => `${j.id}:${j.status}`).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * صف که خالی شد، جدول کهنه است.
   *
   * ⚠️ روی **گذارِ** «چیزی بود ← دیگر نیست» و نه روی هر تغییری: بدونِ
   * این شرط، هر دورِ polling کلِ جدول را دوباره می‌کشد.
   */
  const wasBusy = useRef(false);
  useEffect(() => {
    if (wasBusy.current && running.length === 0) {
      qc.invalidateQueries({ queryKey: ['srcPipeline'] });
      qc.invalidateQueries({ queryKey: ['srcDetail'] });
    }
    wasBusy.current = running.length > 0;
  }, [running.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = pipeline.data ?? [];

  /**
   * فیلترِ مرحله در مرورگر و نه در SQL — چون تعریفِ «مرحله» در
   * `lib/sourcePipeline` است و دو تعریفِ موازی یعنی روزی جدول و فیلترش دو
   * چیز می‌گویند. سیصد ردیف فیلتر شدنی است؛ دو تعریف نه.
   */
  /**
   * تشخیصِ هر ردیف از آخرین تستش — «چه چیزی لازم دارد».
   *
   * ⚠️ در مرورگر حساب می‌شود و نه در SQL، به همان دلیلِ فیلترِ مرحله:
   * قاعده‌اش در `core/lib/sourceTest.needOf` است و آستانه‌هایش عوض
   * می‌شوند. اگر SQL هم حسابش می‌کرد، روزی جدول و پنلِ تست دو چیزِ
   * متفاوت می‌گفتند.
   */
  const needBy = useMemo(() => {
    const map = new Map<string, ReturnType<typeof needOf>>();
    for (const r of rows) map.set(r.key, needOf(r.last_test));
    return map;
  }, [rows]);

  const shown = useMemo(() => {
    let out = stages.length === 0 ? rows : rows.filter((r) => stages.includes(stageOf(r)));
    if (todoOnly) {
      out = out.filter((r) => r.todo?.kind === todoOnly || (todoOnly === 'any' && r.todo));
    }
    if (needs.length > 0) {
      out = out.filter((r) => {
        const need = needBy.get(r.key);
        return need != null && needs.includes(need.key);
      });
    }
    return out;
  }, [rows, stages, needs, needBy, todoOnly]);

  const counts = useMemo(() => {
    const out = Object.fromEntries(STAGE_ORDER.map((s) => [s, 0])) as Record<SourceStage, number>;
    for (const r of rows) out[stageOf(r)] += 1;
    return out;
  }, [rows]);

  /** چند منبع هر تشخیص را دارند — همان عددی که «یک بار کد بنویس» را توجیه می‌کند. */
  const needCounts = useMemo(() => {
    const out = {} as Record<NeedKey, number>;
    for (const need of needBy.values()) {
      if (need) out[need.key] = (out[need.key] ?? 0) + 1;
    }
    return out;
  }, [needBy]);

  /** چند منبع در صفِ استخراجگرند، به تفکیکِ نوع. */
  const todoCounts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const r of rows) if (r.todo) out[r.todo.kind] = (out[r.todo.kind] ?? 0) + 1;
    return out;
  }, [rows]);

  const toggleNeed = (n: NeedKey) =>
    setNeeds(needs.includes(n) ? needs.filter((x) => x !== n) : [...needs, n]);

  // ⚠️ شکلِ تابعیِ setState این‌جا کار نمی‌کند: مقدار در آدرس می‌نشیند و
  // setter مقدارِ نهایی می‌خواهد، نه یک تابع. `stages` از همین رندر
  // خوانده می‌شود که تازه است.
  const toggleStage = (s: SourceStage) =>
    setStages(stages.includes(s) ? stages.filter((x) => x !== s) : [...stages, s]);

  const pending = (jobs.data ?? []).filter((j) => j.status === 'queued' || j.status === 'running');

  return (
    <>
      <div className="card">
        <div className="filters">
          <label className="dim" htmlFor="src-city">شهر</label>
          {/* کشویی و نه فیلدِ متنی، به همان دلیلِ سربرگِ کرالر: یک غلطِ
              تایپی جدولی سراسر خالی می‌دهد که شبیهِ جوابِ درست است. */}
          <select id="src-city" value={city} onChange={(e) => setCity(e.target.value)}>
            <option value="">همه</option>
            {(cities.data ?? []).map((c: PipelineCity) => (
              <option key={c.city} value={c.city}>
                {c.label} ({c.sources}/{c.drafts})
              </option>
            ))}
          </select>

          <input value={q} placeholder="نام یا دامنه" onChange={(e) => setQ(e.target.value)} />

          <span className="spacer" style={{ flex: 1 }} />

          <button className="btn ghost" type="button" onClick={() => setAdding(true)}>
            افزودنِ پیش‌نویس
          </button>
          {/* کشف پول خرج می‌کند (تنها جای زنجیره که می‌کند)، پس عمداً به
              انتخابِ شهر بند است و دکمه‌ی «همه» ندارد. */}
          <button className="btn ghost" type="button" disabled={!city || queue.isPending}
            onClick={() => queue.mutate({ kind: 'discover', target: city })}>
            کشفِ منبعِ تازه
          </button>
          <button className="btn ghost" type="button" disabled={queue.isPending}
            onClick={() => queue.mutate({ kind: 'promote', target: city || null })}>
            ترفیع{city ? `ِ ${cityLabel(city)}` : 'ِ همه'}
          </button>
        </div>

        <div className="chiprow" style={{ marginTop: 10 }}>
          {STAGE_ORDER.map((s) => (
            <button key={s} type="button"
              className={`chip${stages.includes(s) ? ' on' : ''}`}
              title={STAGE_HELP[s]}
              onClick={() => toggleStage(s)}>
              {STAGE_LABEL[s]} ({counts[s]})
            </button>
          ))}
          {stages.length > 0 ? (
            <button className="chip" type="button" onClick={() => setStages([])}>پاک کردن</button>
          ) : null}
        </div>

        {/*
          ⚠️ فیلترِ «چه چیزی لازم دارد» — و عددِ کنارِ هر تشخیص نکته‌ی
          اصلی است، نه خودِ تشخیص: «پارسر لازم دارد ۱۲» یعنی یک بار کد
          نوشتن دوازده منبع را با هم درست می‌کند. بدونِ عدد، این فقط یک
          برچسبِ دیگر بود.

          تشخیص‌هایی که هیچ منبعی ندارند نشان داده نمی‌شوند — فهرستِ
          دوازده‌تاییِ همیشه‌حاضر چیزی به کسی نمی‌گوید.
        */}
        {/*
          ⚠️ این ردیف بالاتر از تگ‌های خودکار است چون تصمیمِ آدم بر حدسِ
          ابزار مقدم است — و چون وقتی می‌نشینی پارسر بنویسی، **همین**
          فهرست است که لازم داری.
        */}
        {Object.keys(todoCounts).length > 0 ? (
          <div className="chiprow" style={{ marginTop: 6 }}>
            <span className="dim" style={{ fontSize: 12 }}>کارِ استخراجگر:</span>
            {(['fix-extractor', 'new-extractor'] as const)
              .filter((k) => todoCounts[k])
              .map((k) => (
                <button key={k} type="button" title={TODO_HELP[k]}
                  className={todoOnly === k ? 'chip on' : 'chip'}
                  onClick={() => setTodoOnly(todoOnly === k ? '' : k)}>
                  {TODO_LABEL[k]} ({todoCounts[k]})
                </button>
              ))}
            {todoOnly ? (
              <button className="chip" type="button"
                onClick={() => setTodoOnly('')}>پاک کردن</button>
            ) : null}
          </div>
        ) : null}

        {Object.keys(needCounts).length > 0 ? (
          <div className="chiprow" style={{ marginTop: 6 }}>
            <span className="dim" style={{ fontSize: 12 }}>لازم دارد:</span>
            {NEED_ORDER.filter((n) => needCounts[n]).map((n) => (
              <button key={n} type="button"
                className={`chip${needs.includes(n) ? ' on' : ''}`}
                onClick={() => toggleNeed(n)}>
                {NEED_LABEL[n]} ({needCounts[n]})
              </button>
            ))}
            {needs.length > 0 ? (
              <button className="chip" type="button"
                onClick={() => setNeeds([])}>پاک کردن</button>
            ) : null}
          </div>
        ) : null}

        {msg ? <p className="note">{msg}</p> : null}

        {pipeline.isError ? (
          <p className="note err">
            خطا: {(pipeline.error as Error)?.message}
            {' — '}اسکیمای <span className="mono">crawler</span> فقط روی دیتابیسِ ابری هست،
            پس این صفحه روی دیتابیسِ لوکال خالی می‌ماند.
          </p>
        ) : null}
      </div>

      <div className="card">
        <h2>خطِ لوله‌ی منابع</h2>
        <p className="dim" style={{ marginBottom: 6 }}>
          هر ردیف یک کاندیدا، از پیش‌نویس تا منبعِ زنده. روی نام کلیک کن تا باز شود.
        </p>

        {pipeline.isLoading ? <p className="dim">…</p> : null}
        {!pipeline.isLoading && shown.length === 0 ? (
          <p className="dim">ردیفی نیست.</p>
        ) : null}

        {shown.length > 0 ? (
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr>
                  <th>شهر</th><th>منبع</th><th>مرحله</th><th>لازم دارد</th>
                  <th>حکم</th><th>گیت‌ها</th>
                  {/* دو عدد در هر خانه: ۲۴ ساعتِ گذشته، و سرجمعِ عمرِ منبع. */}
                  <th className="num">سند<br /><span className="dim">۲۴س / کل</span></th>
                  <th className="num">mention<br /><span className="dim">۲۴س / کل</span></th>
                  <th className="num">منتشر<br /><span className="dim">۲۴س / کل</span></th>
                  <th>آخرین موفقیت</th><th />
                </tr>
              </thead>
              <tbody>
                {shown.map((r: PipelineRow) => {
                  const stage = stageOf(r);
                  const isOpen = open === r.key;
                  const reason = rejectionReason(r);
                  return (
                    <React.Fragment key={r.key}>
                      <tr>
                        <td className="dim">{cityLabel(r.city)}</td>
                        <td>
                          <button className="linkbtn" type="button"
                            onClick={() => setOpen(isOpen ? null : r.key)}>
                            {r.source_label ?? r.draft_label ?? r.bare}
                          </button>
                          <div className="mono dim">{r.bare}</div>
                        </td>
                        <td>
                          <span className={stageClass(stage)} title={STAGE_HELP[stage]}>{STAGE_LABEL[stage]}</span>
                          {(busy.get(r.draft_id ?? '') ?? busy.get(r.bare) ?? []).map((j) => (
                            <span key={j.id} className="chip warn"
                              style={{ marginInlineStart: 4 }}>
                              {JOB_LABEL[j.kind] ?? j.kind}
                              {j.status === 'running' ? ' — در حالِ اجرا' : ' — در صف'}
                            </span>
                          ))}
                        </td>
                        <td>
                          {/* ⚠️ تصمیمِ آدم **بالای** حدسِ ابزار، نه به‌جایش:
                              حدس همچنان کنارش می‌ماند چون گاهی همان
                              می‌گوید کدام کار به‌صرفه‌تر است. */}
                          {r.todo ? (
                            <div className="chip warn" title={TODO_HELP[r.todo.kind]
                              + (r.todo.note ? `

${r.todo.note}` : '')}>
                              {TODO_LABEL[r.todo.kind]}
                            </div>
                          ) : null}
                          <NeedChip need={needBy.get(r.key) ?? null} />
                        </td>
                        <td className="dim mono">
                          {r.verdict ?? '—'}
                          {r.listing_verdict ? ` · ${r.listing_verdict}` : ''}
                        </td>
                        <td className="dim">
                          {/* دو گیتِ `promote`. `—` یعنی هنوز غربال نشده، که با
                              «رد شد» یکی نیست و نباید یک شکل دیده شود. */}
                          {r.looks_usable == null ? '—' : (
                            <>
                              <span className={r.looks_usable ? '' : 'bad'}>
                                {r.looks_usable ? '✅' : '❌'} استخراج
                              </span>
                              {' · '}
                              <span className={r.has_events ? '' : 'bad'}>
                                {r.has_events ? '✅' : '❌'} رویداد
                              </span>
                            </>
                          )}
                        </td>
                        <td className="num"><Pair now={r.documents_last} all={r.documents} /></td>
                        <td className="num"><Pair now={r.mentions_last} all={r.mentions} /></td>
                        <td className="num"><Pair now={r.published_last} all={r.published} /></td>
                        <td className="dim">{ago(r.last_success_at)}</td>
                        <td>
                          <button className="btn ghost" type="button" disabled={queue.isPending}
                            onClick={() => queue.mutate({
                              kind: 'inspect', target: r.draft_id ?? r.bare,
                            })}>بررسی</button>
                          {' '}
                          {/* ⚠️ «ترفیع» فقط برای ردیفی که هنوز در رجیستری
                              نیست. برای منبعِ فعال بی‌معنی است (همین حالا
                              ردیف دارد) و دکمه‌ای که هیچ‌کاری نمی‌کند بدتر
                              از نبودنش است.

                              ‏`target` این‌جا `draft_id` است نه شهر — کرالر
                              از روی پیشوندِ `draft:` می‌فهمد که فقط همین یک
                              ردیف را حکم بدهد. */}
                          {r.draft_id && r.source_version == null ? (
                            <>
                              <button className="btn ghost" type="button"
                                disabled={queue.isPending}
                                onClick={() => queue.mutate({
                                  kind: 'promote', target: r.draft_id,
                                })}>ترفیع</button>
                              {' '}
                            </>
                          ) : null}
                          {/* «تست» ردیف را باز هم می‌کند: نتیجه‌اش همان‌جا
                              می‌آید و دکمه‌ای که جوابش جای دیگری ظاهر شود،
                              دکمه‌ی گمشده است. */}
                          <button className="btn ghost" type="button"
                            onClick={() => { setOpen(r.key); setTesting(r.key); }}>
                            تست
                          </button>
                        </td>
                      </tr>

                      {reason && !isOpen ? (
                        <tr>
                          <td />
                          <td colSpan={11} className="dim">{reason}</td>
                        </tr>
                      ) : null}

                      {isOpen ? (
                        <tr>
                          <td colSpan={10}>
                            <SourceDetail row={r} testNow={testing === r.key}
                              seenReasons={rows.map((x) => x.promote_outcome)}
                              seenTodoNotes={rows.map((x) => x.todo?.note)}
                              onQueued={(m) => {
                                // ⚠ خاموش کردنِ خودکار لازم است: بدونش، بستن و
                                // باز کردنِ دوباره‌ی همین ردیف پنل را با
                                // `autoStart` سوارِ دوباره می‌کند و یک درخواستِ
                                // ناخواسته به سایتِ طرف می‌رود.
                                setTesting(null); setMsg(m); refresh();
                              }} />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <div className="card">
        <h2>صفِ کارها {pending.length > 0 ? `(${pending.length} منتظر)` : null}</h2>
        <p className="dim" style={{ marginBottom: 6 }}>
          این‌ها را کرالر در اجرای بعدی‌اش برمی‌دارد. کارِ تمام‌شده هم این‌جا می‌ماند،
          چون صفِ همیشه‌خالی دو معنی دارد: «همه انجام شد» و «هیچ‌کس برشان نمی‌دارد».
        </p>
        <JobQueue jobs={jobs.data ?? []} onCancel={(id) => cancel.mutate(id)} />
      </div>

      {adding ? (
        <Modal title="افزودنِ پیش‌نویس"
          hint="سایتی که خودت می‌شناسی. مثلِ کاندیدای مدل ثبت می‌شود و همان مسیر را می‌رود."
          onClose={() => setAdding(false)}>
          <AddDraftForm cities={cities.data ?? []} defaultCity={city}
            onDone={(m) => { setAdding(false); setMsg(m); refresh(); }} />
        </Modal>
      ) : null}
    </>
  );
}
